"use client";
import { useEffect, useState } from "react";
import { useAuth } from "../../../../../context/AuthContext";
import { fetchInventoryCategories, createReceivedStock, searchReceivedStock } from "../../../../../api/Inventory/RecieveStockApi";
import { fetchSuppliers } from "../../../../../api/supplierManagement/supplierApi";

export default function useReceiveStock() {
  const { user } = useAuth();
  const roleId = Number(user?.roleid);
  const [activeTab, setActiveTab] = useState("Dispensary");
  const [itemType, setItemType] = useState("");
  const [otcType, setOtcType] = useState("");
  const [frontShopType, setFrontShopType] = useState("");
  const [dispensaryType, setDispensaryType] = useState("");
  const [entryMode, setEntryMode] = useState("Manual");
  const [otherCategorySelected, setOtherCategorySelected] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [scanBarcode, setScanBarcode] = useState("");
  const [scanDrug, setScanDrug] = useState(null);
  const [scanItem, setScanItem] = useState(null);
  const [scanReturnOnSave, setScanReturnOnSave] = useState(false);

  function handleTabChange(tab) {
    setActiveTab(tab);
    // When manually entering, require a fresh choice per tab by clearing previous selections
    if (entryMode === "Manual") {
      setDispensaryType("");
      setFrontShopType("");
      setOtcType("");
      setItemType("");
    }
  }

  async function handleSubmit(payload) {
    setErrors({});

    try {
      const rawType = (payload?.productType || payload?.type || "").toString().toLowerCase();
      const productType = rawType === "drug" ? "Drug" : rawType === "item" ? "Item" : null;

      const apiPayload = {
        ...payload,
        productType,
        branchcode: user?.branchcode ?? null,
        departmentName: activeTab,
        categoryName: payload?.category,
        packSize: payload?.packsize,
        expiryDates: payload?.expirydates,
        invoiceNumber: payload?.invoicenumber,
        shelfNumber: payload?.shelvenumber ?? payload?.shelve,
        supplierCode: payload?.suppliercode,
        activeIngredient: payload?.activeingredient,
        nappiCode: payload?.nappicode,
        dosageForm: payload?.dosageform,
        reorderLevel: payload?.reorderlevel,
        description: payload?.note,
      };

      await createReceivedStock(apiPayload);
      setShowSuccess(true);
      if (scanReturnOnSave) {
        // After a scan-initiated save, return to Scan mode and clear scan state
        setEntryMode("Scan");
        setScanBarcode("");
        setScanDrug(null);
        setScanItem(null);
        setScanReturnOnSave(false);
      }
      return true;
    } catch (err) {
      console.error("Failed to create received stock:", err);
      return false;
    }
  }

  async function handleScanChange(digits) {
    const value = String(digits || "");
    setScanBarcode(value);

    // Only trigger lookup when we have exactly 12 digits
    if (value.length !== 12) return;

    try {
      const departmentName = activeTab;

      // Try Drug first
      let found = await searchReceivedStock("Drug", value, departmentName);
      if (found) {
        setScanDrug(found);
        setScanItem(null);
        setScanReturnOnSave(true);
        setEntryMode("Manual");
        setDispensaryType("Drug");
        return;
      }

      // Then try Item
      found = await searchReceivedStock("Item", value, departmentName);
      if (found) {
        setScanItem(found);
        setScanDrug(null);
        setScanReturnOnSave(true);
        setEntryMode("Manual");

        if (activeTab === "Dispensary") {
          setDispensaryType("Item");
        } else if (activeTab === "Front Shop") {
          setFrontShopType("Item");
        } else if (activeTab === "Clinic") {
          setItemType("Item");
        } else if (activeTab === "OTC") {
          setOtcType("Item");
        }
        return;
      }

      // Not found: treat as new item and go straight to the relevant form
      let selectedType = null;
      if (activeTab === "Dispensary") selectedType = dispensaryType;
      else if (activeTab === "Front Shop") selectedType = frontShopType;
      else if (activeTab === "OTC") selectedType = otcType;
      else if (activeTab === "Clinic") selectedType = roleId === 6 ? "Drug" : itemType;

      // Fallback to Item if somehow still not set
      if (selectedType !== "Drug" && selectedType !== "Item") selectedType = "Item";

      setScanReturnOnSave(true);
      setEntryMode("Manual");

      if (selectedType === "Drug") {
        setScanDrug({ barcode: value });
        setScanItem(null);
        setDispensaryType("Drug");
      } else {
        setScanItem({ barcode: value });
        setScanDrug(null);

        if (activeTab === "Dispensary") {
          setDispensaryType("Item");
        } else if (activeTab === "Front Shop") {
          setFrontShopType("Item");
        } else if (activeTab === "Clinic") {
          setItemType("Item");
        } else if (activeTab === "OTC") {
          setOtcType("Item");
        }
      }
    } catch (err) {
      console.error("Scan search error:", err);
    }
  }

  // Whenever we enter Scan mode from Manual or when the page first loads,
  // ensure the scan input and any prefill state are cleared.
  useEffect(() => {
    if (entryMode === "Scan") {
      setScanBarcode("");
      setScanDrug(null);
      setScanItem(null);
      setScanReturnOnSave(false);
    }
  }, [entryMode]);

  // Force Clinic for role 6 and remove filters
  useEffect(() => {
    if (roleId === 6 && activeTab !== "Clinic") {
      setActiveTab("Clinic");
      setItemType("");
      setOtcType("");
    }
  }, [roleId, activeTab]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cats = await fetchInventoryCategories();
        if (mounted && Array.isArray(cats)) {
          setCategories(cats);
        }
      } catch (e) {
        console.error("Failed to load inventory categories", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const rows = await fetchSuppliers();
        if (mounted && Array.isArray(rows)) {
          setSuppliers(rows);
        }
      } catch (e) {
        console.error("Failed to load suppliers", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return {
    roleId,
    activeTab,
    handleTabChange,
    entryMode,
    setEntryMode,
    dispensaryType,
    setDispensaryType,
    itemType,
    setItemType,
    frontShopType,
    setFrontShopType,
    otcType,
    setOtcType,
    otherCategorySelected,
    setOtherCategorySelected,
    categories,
    suppliers,
    errors,
    handleSubmit,
    showSuccess,
    setShowSuccess,
    scanBarcode,
    onScanChange: handleScanChange,
    scanDrug,
    scanItem,
    scanReturnOnSave,
  };
}
